import type { Node, NodeProps } from "@xyflow/react";

import { Badge } from "@/components/ui/badge";
import { BADGE_TONES } from "@/lib/badge-tones";
import { formatCurrency, formatDate, formatStatusLabel } from "@/lib/format";

import { FlowNodeCard, FlowNodeRow, LeftTargetHandle, RightSourceHandle } from "./shared";
import type { OrderNodeData } from "./types";

type OrderFlowNode = Node<OrderNodeData, "order">;

function getOrderBadgeClass(status?: string | null): string {
  const normalized = (status ?? "").trim().toLowerCase();
  if (normalized === "pending_payment") return BADGE_TONES.yellow;
  if (normalized === "awaiting_inventory") return BADGE_TONES.amber;
  if (normalized === "ready_to_prepare") return BADGE_TONES.blue;
  if (normalized === "in_transit") return BADGE_TONES.violet;
  if (normalized === "delivered") return BADGE_TONES.emerald;
  if (normalized === "cancelled" || normalized === "canceled") return BADGE_TONES.red;
  return BADGE_TONES.slate;
}

export function OrderNode({ data }: NodeProps<OrderFlowNode>) {
  return (
    <>
      <LeftTargetHandle />
      <RightSourceHandle />
      <FlowNodeCard
        title="Order"
        className="border-primary/40"
        rightSlot={
          <Badge variant="outline" className={getOrderBadgeClass(data.status)}>
            {formatStatusLabel(data.status, "Unknown")}
          </Badge>
        }
      >
        <p className="text-sm font-semibold">{data.orderNumber || "Order"}</p>
        <FlowNodeRow
          label="Total"
          value={typeof data.totalAmount === "number" ? formatCurrency(data.totalAmount) : "-"}
        />
        <FlowNodeRow
          label="Created"
          value={
            data.createdAt
              ? formatDate(data.createdAt, {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })
              : "-"
          }
        />
      </FlowNodeCard>
    </>
  );
}
